import PropTypes from 'prop-types';
import * as d3 from 'd3';
import { find, findIndex, forEach, get, isString, map, isEmpty } from 'lodash';

import ChartComponent from '../ChartComponent';

import { DEFAULT_SELECTED_ELEMENTS } from '../../../api/Graphics/graphic-operations';

const CARD_MARGIN = 5;
const TOP_MARGIN = 15;
const LEFT_MARGIN = 100;
const BOTTOM_MARGIN = 65;
const RIGHT_MARGIN = 100;
const TEXT_INICIAL_SIZE = 10;
const CHART_CLASS = 'stacked-bar-chart';

class StackedBarChart extends ChartComponent {
  id = isEmpty(this.props.atlasID)
    ? `${CHART_CLASS}-card-${this.props.isCard}`
    : this.props.atlasID;

  drawChart = () => {
    const {
      alpha,
      borderAlpha,
      borderColor,
      colorRange,
      dataNamed,
      dataNumeric,
      height,
      isCard,
      numericName,
      globalProperties,
      selectedElements,
      weight,
      width
    } = this.props;

    const margin = {
      top: isCard ? CARD_MARGIN : TOP_MARGIN,
      left: isCard ? CARD_MARGIN : LEFT_MARGIN,
      bottom: isCard ? CARD_MARGIN : BOTTOM_MARGIN,
      right: isCard ? CARD_MARGIN : RIGHT_MARGIN
    };

    const numericNamesAsObjects = map(numericName, name => ({ name }));
    const data = map(dataNamed, (namedValue, namedIndex) => {
      const objectToReturn = {
        name: namedValue
      };
      forEach(dataNumeric, (dataNumericSubarray, numericSubarrayIndex) => {
        objectToReturn[numericName[numericSubarrayIndex]] = dataNumericSubarray[namedIndex];
      });
      return objectToReturn;
    });

    const svg = d3.select(`#svg-${this.id}`).attr('viewBox', [0, 0, width, height]);

    const series = d3
      .stack()
      .keys(numericName)(data)
      // eslint-disable-next-line no-return-assign, no-sequences, no-param-reassign
      .map(d => (d.forEach(v => (v.key = d.key)), d));

    const x = d3
      .scaleBand()
      .domain(map(data, d => d.name))
      .range([margin.left, width - margin.right])
      .paddingOuter(isCard ? 0 : 0.3)
      .paddingInner(0.2);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(series, d => d3.max(d, d2 => d2[1]))])
      .nice()
      .range([height - margin.bottom, margin.top]);

    const xAxis = selectedElements.Text
      ? d3 /* eslint-disable indent */
          .axisBottom(x)
          .tickSizeOuter(0) /* eslint-enable indent */
      : d3.axisBottom(x).tickValues([]);

    const yAxis = selectedElements.Text
      ? d3 /* eslint-disable indent */
          .axisLeft(y)
          .ticks() /* eslint-enable indent */
      : d3.axisLeft(y).tickValues([]);

    const bars = svg
      .append('g')
      .attr('class', `${this.id}`)
      .selectAll('rect')
      .data(series)
      .join('g')
      .selectAll('rect')
      .data(d => d)
      .join('rect')
      .attr('class', `${this.id}`)
      .attr('fill', (d, i) => {
        if (isString(colorRange)) {
          return colorRange;
        }
        if (isString(colorRange[findIndex(numericNamesAsObjects, ['name', d.key])])) {
          return colorRange[findIndex(numericNamesAsObjects, ['name', d.key])];
        }
        return colorRange[findIndex(numericNamesAsObjects, ['name', d.key])][i];
      })
      .attr('fill-opacity', d => alpha[findIndex(numericNamesAsObjects, ['name', d.key])])
      .attr('stroke', d => borderColor[findIndex(numericNamesAsObjects, ['name', d.key])])
      .attr('stroke-opacity', d => borderAlpha[findIndex(numericNamesAsObjects, ['name', d.key])])
      .attr('stroke-width', d => weight[findIndex(numericNamesAsObjects, ['name', d.key])])
      .attr('x', d => x(d.data.name))
      .attr('y', d => Math.min(y(d[0]), y(d[1])))
      .attr('height', d => Math.abs(y(d[0]) - y(d[1])))
      .attr('width', x.bandwidth());

    if (!isCard) {
      bars.append('title').text(d => `${d.data.name} ${d.key}: ${d.data[d.key]}`);

      svg
        .append('g')
        .attr('class', `${this.id}`)
        .attr('transform', `translate(0,${y(0)})`)
        .call(xAxis);

      svg
        .selectAll('.tick')
        .selectAll('text')
        .style('text-anchor', 'start')
        .attr(
          'transform',
          `rotate(${get(
            find(globalProperties, ['name', 'xAxisTextAngle']),
            'value',
            TEXT_INICIAL_SIZE
          )})`
        );

      svg
        .append('g')
        .attr('class', `${this.id}`)
        .attr('transform', `translate(${margin.left},0)`)
        .call(yAxis);

      svg
        .selectAll('.tick')
        .selectAll('text')
        .attr(
          'font-size',
          get(find(globalProperties, ['name', 'textSize']), 'value', TEXT_INICIAL_SIZE)
        );

      if (!selectedElements.Axis) {
        svg.selectAll('.domain').remove();
      }
    }
  };
}

StackedBarChart.defaultProps = {
  width: 0,
  height: 0,
  dataNamed: ['A', 'B', 'C'],
  dataNumeric: [
    [1, 1.5, 2],
    [1.1, 1.5, 2],
    [1.2, 1.5, 2]
  ],
  colorRange: ['lightsalmon', 'tomato', 'lightcoral'],
  alpha: [0.9, 0.9, 0.9],
  weight: [1, 1, 1],
  numericName: ['AtributoA', 'AtributoB', 'AtributoC'],
  borderColor: ['black', 'black', 'black'],
  borderAlpha: [0.9, 0.9, 0.9],
  selectedElements: DEFAULT_SELECTED_ELEMENTS,
  globalProperties: [],
  isCard: false
};
StackedBarChart.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  dataNumeric: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.number),
    PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number))
  ]),
  dataNamed: PropTypes.array,
  colorRange: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]))
  ]),
  alpha: PropTypes.arrayOf(PropTypes.number),
  weight: PropTypes.arrayOf(PropTypes.number),
  borderColor: PropTypes.arrayOf(PropTypes.string),
  borderAlpha: PropTypes.arrayOf(PropTypes.number),
  selectedElements: PropTypes.object,
  globalProperties: PropTypes.array,
  numericName: PropTypes.arrayOf(PropTypes.string),
  isCard: PropTypes.bool
};

export default StackedBarChart;
